import * as React from 'react';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Card,
  IconButton,
  InputAdornment,
  Pagination,
  Switch,
  Table,
  TableBody,
  TableContainer,
  TablePagination,
  TextField,
  Typography,
  Chip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import RestoreOutlinedIcon from '@mui/icons-material/RestoreOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { useNavigate } from 'react-router-dom';

import { useTableState } from 'hooks/useTableState';
import {
  StyledTableCell,
  StyledTableRow,
} from '../../../../../../commons/generics/StyledTableComponents';
import { GenericTableHead, HeadCell } from '../../../../../../commons/generics/GenericTableHead';
import { SinDatos } from '../../../../../../commons/generics/SinDatos';
import { StatusChip } from '../../../../../../commons/generics/StatusChip';
import { hasPemissions } from '../../../../../../commons/utils';
import { paddingCardsTables } from '../../../../../../commons';
import { ModalConfirmDeactivation } from '../../../../../../components/modals/ModalConfirmDeactivation';
import { ModalConfirmRestore } from '../../../../../../components/modals/ModalConfirmRestore';
import {
  deleteGlobalConfigStore,
  getAllGlobalConfigStore,
  restoreGlobalConfigStore,
} from 'store/services-ia-global-config';

export const TableAjustesGlobalesIa = () => {
  const dispatch: any = useDispatch();
  const navigate = useNavigate();
  const { global_config_list }: any = useSelector((state: any) => state.servicesIaGlobalConfig);

  const [hasPermission, setHasPermission] = useState(true);
  const [canView, setCanView] = useState(false);
  const [canEdit, setCanEdit] = useState(false);
  const [canDelete, setCanDelete] = useState(false);
  const [canRestore, setCanRestore] = useState(false);

  const {
    dataTableList,
    openElem,
    setOpenElem,
    checked,
    setChecked,
    searchTerm,
    setSearchTerm,
    order,
    orderBy,
    handleSwitchChange,
    handleSearch: handleSearchHook,
    handleResetSearch: handleResetSearchHook,
    page,
    rowsPerPage,
    handleChangePage,
    handleChangeRowsPerPage,
    pagePagination,
    itemCountPagination,
    changePaginationPage: changePaginationPageHook,
    handleRequestSort,
    updatePaginationFromList,
    openModalDeactivation,
    setOpenModalDeactivation,
    openModalRestore,
    setOpenModalRestore,
    handleResponse,
    getSortedAndFilteredList,
    isServerSide,
  } = useTableState('ajustes_globales_ia_table');

  const [inputValue, setInputValue] = useState('');

  const checkPermissions = async () => {
    const view = await hasPemissions(
      'modulo.manager',
      'modulo.manager.plataforma.ia',
      'view',
      'modulo.manager.plataforma.ia.configuracion',
      'modulo.manager.plataforma.ia.configuracion.ajustes'
    );
    if (!view) setHasPermission(false);
    else setCanView(true);

    const edit = await hasPemissions(
      'modulo.manager',
      'modulo.manager.plataforma.ia',
      'edit',
      'modulo.manager.plataforma.ia.configuracion',
      'modulo.manager.plataforma.ia.configuracion.ajustes'
    );
    if (edit) setCanEdit(true);

    const del = await hasPemissions(
      'modulo.manager',
      'modulo.manager.plataforma.ia',
      'delete',
      'modulo.manager.plataforma.ia.configuracion',
      'modulo.manager.plataforma.ia.configuracion.ajustes'
    );
    if (del) {
      setCanDelete(true);
      setCanRestore(true);
    }
  };

  useEffect(() => {
    checkPermissions();
  }, []);

  const getInitialData = async (statusVal = checked ? 0 : 1) => {
    const response = await dispatch(
      getAllGlobalConfigStore({ page: 1, take: rowsPerPage, status: statusVal, param: '' })
    );
    await handleResponse(response, 'obtener lista de ajustes globales', false);
  };

  useEffect(() => {
    if (canView) {
      getInitialData();
    }
  }, [canView]);

  useEffect(() => {
    updatePaginationFromList(global_config_list);
  }, [global_config_list, updatePaginationFromList]);

  const handleSearch = async (param: string) => {
    setSearchTerm(param);
    setInputValue(param);
    handleSearchHook(
      param,
      dispatch,
      (p: string) =>
        getAllGlobalConfigStore({ page: 1, take: rowsPerPage, status: checked ? 0 : 1, param: p }),
      getInitialData,
      null,
      'buscar ajustes globales'
    );
  };

  const onSwitchChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    handleSwitchChange(e);
    const newChecked = e.target.checked;
    const status = newChecked ? 0 : 1;

    if (searchTerm) {
      handleSearchHook(
        searchTerm,
        dispatch,
        (p: string) => getAllGlobalConfigStore({ page: 1, take: rowsPerPage, status, param: p }),
        () => getInitialData(status),
        null,
        'buscar ajustes globales'
      );
    } else {
      getInitialData(status);
    }
  };

  const onResetSearch = () => {
    setInputValue('');
    handleResetSearchHook(() => getInitialData(checked ? 0 : 1));
  };

  const changePaginationPage = async (event: React.ChangeEvent<unknown>, value: number) => {
    const validPage = value < 1 ? 1 : value;
    changePaginationPageHook(event, validPage);
    const response = await dispatch(
      getAllGlobalConfigStore({
        page: validPage,
        take: rowsPerPage,
        status: checked ? 0 : 1,
        param: searchTerm,
      })
    );
    await handleResponse(response, 'listar ajustes globales', false);
  };

  const confirmDeactivate = async () => {
    if (!openElem) return;
    const response = await dispatch(
      deleteGlobalConfigStore(openElem.id, {
        page: pagePagination,
        take: rowsPerPage,
        status: checked ? 0 : 1,
        param: searchTerm,
      })
    );
    await handleResponse(response, 'desactivar configuración', true);
    setOpenModalDeactivation(false);
    setOpenElem(null);
  };

  const confirmRestore = async () => {
    if (!openElem) return;
    const response = await dispatch(
      restoreGlobalConfigStore(openElem.id, {
        page: pagePagination,
        take: rowsPerPage,
        status: checked ? 0 : 1,
        param: searchTerm,
      })
    );
    await handleResponse(response, 'restaurar configuración', true);
    setOpenModalRestore(false);
    setOpenElem(null);
  };

  const handleView = (row: any) => {
    navigate(`/manager/plataforma-ia/configuracion/ajustes/${row.id}/detalle`);
  };

  const handleEdit = (row: any) => {
    navigate(`/manager/plataforma-ia/configuracion/ajustes/${row.id}/editar`);
  };

  const headCells: HeadCell[] = [
    { id: 'index', label: '#' },
    { id: 'creditsPerUsd', label: 'Créditos por USD' },
    { id: 'defaultTemperature', label: 'Temperatura' },
    { id: 'defaultMaxTokens', label: 'Tokens Máximos' },
    { id: 'isActive', label: 'Estado' },
    { id: 'actions', label: 'Acciones', align: 'center' },
  ];

  const sortedList = getSortedAndFilteredList(
    dataTableList || [],
    ['creditsPerUsd', 'defaultTemperature', 'defaultMaxTokens'],
    'status',
    checked ? 0 : 1
  );

  if (!hasPermission) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6" color="textSecondary">
          No tienes permisos para ver este contenido.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', mt: paddingCardsTables }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3, alignItems: 'center' }}>
        <TextField
          placeholder="Buscar..."
          variant="outlined"
          size="small"
          autoComplete="off"
          value={inputValue}
          onChange={(e) => handleSearch(e.target.value)}
          sx={{ width: 400 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: inputValue && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={onResetSearch}>
                  <ClearIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Switch checked={checked} onChange={onSwitchChange} color="primary" />
          <Typography variant="body2" sx={{ color: '#4B5563', fontWeight: 500 }}>
            Mostrar solo inactivos
          </Typography>
        </Box>
      </Box>

      <Card
        sx={{
          borderRadius: '12px',
          border: '1px solid #E5E7EB',
          boxShadow: 'none',
          overflow: 'hidden',
        }}
      >
        <TableContainer>
          <Table>
            <GenericTableHead
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
              headCells={headCells}
            />
            <TableBody>
              {sortedList.length > 0 ? (
                sortedList
                  .slice(
                    isServerSide ? 0 : page * rowsPerPage,
                    isServerSide ? rowsPerPage : page * rowsPerPage + rowsPerPage
                  )
                  .map((row: any, index: number) => {
                    const currentIndex = isServerSide
                      ? (pagePagination - 1) * 10 + index + 1
                      : index + 1;

                    return (
                      <StyledTableRow key={row.id}>
                        <StyledTableCell component="th" scope="row">
                          {currentIndex}
                        </StyledTableCell>
                        <StyledTableCell>{row.creditsPerUsd ?? 'No definido'}</StyledTableCell>
                        <StyledTableCell>{row.defaultTemperature ?? 'No definido'}</StyledTableCell>
                        <StyledTableCell>{row.defaultMaxTokens ?? 'No definido'}</StyledTableCell>
                        <StyledTableCell>
                          <StatusChip status={row.status} />
                        </StyledTableCell>

                        <StyledTableCell align="center">
                          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                            <span style={{ cursor: !canView ? 'not-allowed' : 'pointer' }}>
                              <IconButton
                                size="small"
                                onClick={() => canView && handleView(row)}
                                disabled={!canView}
                                color="info"
                              >
                                <VisibilityOutlinedIcon fontSize="small" />
                              </IconButton>
                            </span>

                            <span
                              style={{
                                cursor: !canEdit || row.status !== 1 ? 'not-allowed' : 'pointer',
                              }}
                            >
                              <IconButton
                                size="small"
                                onClick={() => canEdit && row.status === 1 && handleEdit(row)}
                                disabled={!canEdit || row.status !== 1}
                                color="primary"
                              >
                                <EditOutlinedIcon fontSize="small" />
                              </IconButton>
                            </span>

                            {row.status === 1 ? (
                              <span style={{ cursor: !canDelete ? 'not-allowed' : 'pointer' }}>
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    if (canDelete) {
                                      setOpenElem(row);
                                      setOpenModalDeactivation(true);
                                    }
                                  }}
                                  disabled={!canDelete}
                                  color="error"
                                >
                                  <DeleteOutlineOutlinedIcon fontSize="small" />
                                </IconButton>
                              </span>
                            ) : (
                              <span style={{ cursor: !canRestore ? 'not-allowed' : 'pointer' }}>
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    if (canRestore) {
                                      setOpenElem(row);
                                      setOpenModalRestore(true);
                                    }
                                  }}
                                  disabled={!canRestore}
                                  color="success"
                                >
                                  <RestoreOutlinedIcon fontSize="small" />
                                </IconButton>
                              </span>
                            )}
                          </Box>
                        </StyledTableCell>
                      </StyledTableRow>
                    );
                  })
              ) : (
                <StyledTableRow>
                  <StyledTableCell colSpan={6} sx={{ p: 0 }}>
                    <SinDatos />
                  </StyledTableCell>
                </StyledTableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {dataTableList.length > 0 && (
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={itemCountPagination}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Filas por página"
        />
      )}

      {dataTableList.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            mt: 1,
            padding: 2.5,
            borderTop: 1,
            borderColor: 'divider',
          }}
        >
          <Pagination
            sx={{ justifyContent: 'center' }}
            color="primary"
            count={itemCountPagination}
            page={pagePagination}
            onChange={changePaginationPage}
            showFirstButton
            showLastButton
            shape="circular"
          />
        </Box>
      )}

      <ModalConfirmDeactivation
        open={openModalDeactivation}
        setOpen={setOpenModalDeactivation}
        title="¿Estás seguro que deseas desactivar esta configuración global?"
        description="Este elemento pasará a estado inactivo."
        onConfirm={confirmDeactivate}
      />
      <ModalConfirmRestore
        open={openModalRestore}
        setOpen={setOpenModalRestore}
        title="¿Estás seguro que deseas restaurar esta configuración global?"
        description="Este elemento pasará a estado activo."
        onConfirm={confirmRestore}
      />
    </Box>
  );
};
